import { useEffect, useRef, useState } from 'react';
import { ApiError } from '/src/api';
import { notifyUnauthorized } from '/src/api/core/sessionEvents';
import {
  CalculateCreditsService,
  TCalculateCreditsRequest,
  TMissingDataResponse,
} from '/src/api/services/CalculateCreditsService';
import { useGlobalStore } from '/src/context';
import { toastMessages } from '/src/lib/toast';

export type CreditCalculationStatus = 'idle' | 'submitting' | 'running' | 'done' | 'failed' | 'cancelled';

const CONNECTION_LOST_MESSAGE =
  'The connection was lost before the calculation finished. All changes were rolled back — re-run the calculation.';

const isMissingDataResponse = (body: unknown): body is TMissingDataResponse =>
  typeof body === 'object' && body !== null && 'missing_cixd_standards' in body;

type StreamEvent =
  | { type: 'line'; line: string }
  | { type: 'done'; updated_count: number }
  | { type: 'failed'; message: string }
  | { type: 'cancelled' };

const parseStreamEvents = (rawEvents: string[]): StreamEvent[] => {
  const events: StreamEvent[] = [];

  for (const rawEvent of rawEvents) {
    let data = '';

    for (const line of rawEvent.split('\n')) {
      if (line.startsWith('data: ')) {
        data += line.slice('data: '.length);
      }
    }

    if (data !== '') {
      events.push(JSON.parse(data));
    }
  }

  return events;
};

export const useCreditCalculation = () => {
  const globalStore = useGlobalStore();
  const [status, setStatus] = useState<CreditCalculationStatus>('idle');
  const [logLines, setLogLines] = useState<string[]>([]);
  const [updatedCount, setUpdatedCount] = useState<number | null>(null);
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [missingData, setMissingData] = useState<TMissingDataResponse | null>(null);
  const streamAbortRef = useRef<AbortController | null>(null);

  const closeStream = () => {
    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
  };

  useEffect(() => closeStream, []);

  const calculate = async (payload: TCalculateCreditsRequest) => {
    setStatus('submitting');
    setLogLines([]);
    setUpdatedCount(null);
    setFailureMessage(null);
    setMissingData(null);
    closeStream();

    const abortController = new AbortController();
    streamAbortRef.current = abortController;

    const handleTerminalEvent = (event: Exclude<StreamEvent, { type: 'line' }>) => {
      if (event.type === 'done') {
        setUpdatedCount(event.updated_count);
        setStatus('done');
      } else if (event.type === 'cancelled') {
        setStatus('cancelled');
      } else {
        setFailureMessage(event.message);
        setStatus('failed');
      }
    };

    try {
      const response = await CalculateCreditsService.calculate(payload, abortController.signal);
      const contentType = response.headers.get('content-type') ?? '';

      if (!response.ok || !contentType.includes('text/event-stream') || response.body === null) {
        const body = await response.json().catch(() => null);

        setStatus('idle');

        if (response.status === 401) {
          notifyUnauthorized(body);
          return;
        }

        if (response.status === 400 && isMissingDataResponse(body)) {
          setMissingData(body);
          return;
        }

        globalStore.handleApiError(
          { status: response.status, body } as unknown as ApiError,
          toastMessages.calculateCredits.error
        );
        return;
      }

      setStatus('running');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const rawEvents = buffer.split('\n\n');
        buffer = rawEvents.pop() ?? '';

        const newLines: string[] = [];
        let terminalEvent: Exclude<StreamEvent, { type: 'line' }> | undefined;

        for (const event of parseStreamEvents(rawEvents)) {
          if (event.type === 'line') {
            newLines.push(event.line);
          } else {
            terminalEvent = event;
            break;
          }
        }

        if (newLines.length > 0) {
          setLogLines((lines) => [...lines, ...newLines]);
        }

        if (terminalEvent !== undefined) {
          handleTerminalEvent(terminalEvent);
          return;
        }
      }

      setFailureMessage(CONNECTION_LOST_MESSAGE);
      setStatus('failed');
    } catch (error) {
      if (abortController.signal.aborted) return;

      setFailureMessage(CONNECTION_LOST_MESSAGE);
      setStatus('failed');
    } finally {
      if (streamAbortRef.current === abortController) {
        streamAbortRef.current = null;
      }
    }
  };

  const cancel = () => {
    if (streamAbortRef.current === null) return;

    closeStream();
    setStatus('cancelled');
  };

  return { status, logLines, updatedCount, failureMessage, missingData, calculate, cancel };
};
